import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fmtMoney, today } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";

const blankLine = () => ({ item_name: "", quantity: 1, rate: 0, discount_percent: 0, tax_rate: 18, tax_type: "GST", hsn: "" });

export default function InvoiceForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [customers, setCustomers] = useState([]);
    const [items, setItems] = useState([]);
    const [form, setForm] = useState({
        type: "invoice",
        customer_id: "",
        date: today(),
        due_date: today(),
        currency: "INR",
        exchange_rate: 1,
        is_inter_state: false,
        is_export: false,
        place_of_supply: "Maharashtra",
        notes: "Thank you for your business.",
        terms: "Payment due as per terms.",
        shipping: 0,
        lines: [blankLine()],
    });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/customers").then(({ data }) => setCustomers(data));
        api.get("/items").then(({ data }) => setItems(data));
        if (id) api.get(`/invoices/${id}`).then(({ data }) => setForm(data));
    }, [id]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const setLine = (i, k, v) => setForm((f) => {
        const lines = [...f.lines];
        lines[i] = { ...lines[i], [k]: v };
        return { ...f, lines };
    });
    const addLine = () => set("lines", [...form.lines, blankLine()]);
    const rmLine = (i) => set("lines", form.lines.filter((_, idx) => idx !== i));

    const pickItem = (i, itemId) => {
        const it = items.find((x) => x.id === itemId);
        if (!it) return;
        setForm((f) => {
            const lines = [...f.lines];
            lines[i] = { ...lines[i], item_id: it.id, item_name: it.name, hsn: it.hsn || it.sac, rate: it.sales_price, tax_rate: it.tax_rate };
            return { ...f, lines };
        });
    };

    // Compute preview totals
    const preview = (() => {
        let sub = 0, tax = 0;
        for (const l of form.lines) {
            const gross = (l.quantity || 0) * (l.rate || 0);
            const disc = gross * (l.discount_percent || 0) / 100;
            const amt = gross - disc;
            sub += amt;
            tax += amt * (l.tax_rate || 0) / 100;
        }
        return { sub, tax, total: sub + tax + (Number(form.shipping) || 0) };
    })();

    const onCustomer = (cid) => {
        const c = customers.find((x) => x.id === cid);
        if (!c) return set("customer_id", cid);
        setForm((f) => ({ ...f, customer_id: cid, currency: c.currency, exchange_rate: c.currency === "INR" ? 1 : (c.currency === "USD" ? 83.25 : c.currency === "EUR" ? 90.10 : 1), place_of_supply: c.place_of_supply || f.place_of_supply, is_inter_state: c.place_of_supply ? !c.place_of_supply.toLowerCase().includes("maharashtra") : false }));
    };

    const save = async () => {
        if (!form.customer_id) return toast.error("Pick a customer");
        if (!form.lines?.length) return toast.error("Add at least one line");
        setBusy(true);
        try {
            if (id) await api.put(`/invoices/${id}`, form);
            else await api.post(`/invoices`, form);
            toast.success("Invoice saved");
            navigate("/sales/invoices");
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
        finally { setBusy(false); }
    };

    return (
        <>
            <PageHeader
                kicker={id ? "EDIT INVOICE" : "NEW INVOICE"}
                breadcrumbs={<button className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1" onClick={() => navigate("/sales/invoices")}><ArrowLeft className="w-3.5 h-3.5" />Invoices</button>}
                title={id ? "Edit invoice" : "Compose a new invoice"}
                actions={<><Button variant="outline" onClick={() => navigate("/sales/invoices")}>Cancel</Button><Button onClick={save} disabled={busy} data-testid="invoice-save"><Save className="w-4 h-4 mr-1.5" />{busy ? "Saving…" : "Save"}</Button></>}
            />
            <PageBody>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-2 bg-card border border-border rounded-md p-6 space-y-5">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <div className="col-span-2 md:col-span-1">
                                <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Customer</Label>
                                <Select value={form.customer_id} onValueChange={onCustomer}>
                                    <SelectTrigger className="mt-1.5" data-testid="invoice-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                                    <SelectContent>
                                        {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} • {c.currency}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Issue date</Label>
                                <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="mt-1.5" />
                            </div>
                            <div>
                                <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Due date</Label>
                                <Input type="date" value={form.due_date || ""} onChange={(e) => set("due_date", e.target.value)} className="mt-1.5" />
                            </div>
                            <div>
                                <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Currency</Label>
                                <Input value={form.currency} readOnly className="mt-1.5 bg-muted/40" />
                            </div>
                            <div>
                                <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Exchange rate</Label>
                                <Input type="number" step="0.0001" value={form.exchange_rate} onChange={(e) => set("exchange_rate", Number(e.target.value))} className="mt-1.5" />
                            </div>
                            <div>
                                <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Place of supply</Label>
                                <Input value={form.place_of_supply || ""} onChange={(e) => set("place_of_supply", e.target.value)} className="mt-1.5" />
                            </div>
                            <div className="col-span-2 md:col-span-3 flex items-center gap-6 pt-1">
                                <label className="flex items-center gap-2 text-[12.5px]"><Switch checked={form.is_inter_state} onCheckedChange={(v) => set("is_inter_state", v)} />Inter-state (IGST)</label>
                                <label className="flex items-center gap-2 text-[12.5px]"><Switch checked={form.is_export} onCheckedChange={(v) => set("is_export", v)} />Export supply</label>
                                <label className="flex items-center gap-2 text-[12.5px]"><Switch checked={form.reverse_charge} onCheckedChange={(v) => set("reverse_charge", v)} />Reverse charge</label>
                            </div>
                        </div>

                        <div className="border-t border-border pt-5">
                            <div className="flex items-center justify-between mb-3">
                                <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Line items</Label>
                                <Button size="sm" variant="outline" onClick={addLine} data-testid="invoice-add-line"><Plus className="w-3.5 h-3.5 mr-1" />Add line</Button>
                            </div>
                            {/* Column headers */}
                            <div className="hidden md:grid grid-cols-12 gap-2 px-1 pb-2 mb-1 border-b border-border text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">
                                <div className="col-span-4">Item & description</div>
                                <div className="col-span-1 text-right">Qty</div>
                                <div className="col-span-2 text-right">Rate</div>
                                <div className="col-span-1 text-right">Disc %</div>
                                <div className="col-span-1 text-right">GST %</div>
                                <div className="col-span-2 text-right">Amount</div>
                                <div className="col-span-1"></div>
                            </div>
                            <div className="space-y-2">
                                {form.lines.map((l, i) => (
                                    <div key={i} className="grid grid-cols-12 gap-2 items-start" data-testid={`invoice-line-${i}`}>
                                        <div className="col-span-12 md:col-span-4">
                                            <Select value={l.item_id || ""} onValueChange={(v) => pickItem(i, v)}>
                                                <SelectTrigger className="h-9"><SelectValue placeholder="Pick item or type" /></SelectTrigger>
                                                <SelectContent>
                                                    {items.slice(0, 30).map((it) => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                            <Input className="mt-1.5 h-9" placeholder="Description" value={l.item_name} onChange={(e) => setLine(i, "item_name", e.target.value)} />
                                        </div>
                                        <Input className="col-span-3 md:col-span-1 h-9 text-right" type="number" placeholder="Qty" value={l.quantity} onChange={(e) => setLine(i, "quantity", Number(e.target.value))} />
                                        <Input className="col-span-4 md:col-span-2 h-9 text-right" type="number" placeholder="Rate" value={l.rate} onChange={(e) => setLine(i, "rate", Number(e.target.value))} />
                                        <Input className="col-span-3 md:col-span-1 h-9 text-right" type="number" placeholder="Disc %" value={l.discount_percent} onChange={(e) => setLine(i, "discount_percent", Number(e.target.value))} />
                                        <Input className="col-span-3 md:col-span-1 h-9 text-right" type="number" placeholder="GST %" value={l.tax_rate} onChange={(e) => setLine(i, "tax_rate", Number(e.target.value))} />
                                        <div className="col-span-6 md:col-span-2 h-9 flex items-center text-[13px] font-mono justify-end pr-1 text-muted-foreground">
                                            {fmtMoney((l.quantity || 0) * (l.rate || 0) * (1 - (l.discount_percent || 0) / 100) * (1 + (l.tax_rate || 0) / 100), form.currency)}
                                        </div>
                                        <Button variant="ghost" size="icon" className="col-span-3 md:col-span-1 h-9 w-9 justify-self-end text-muted-foreground hover:text-destructive" onClick={() => rmLine(i)} data-testid={`invoice-line-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 border-t border-border pt-5">
                            <div>
                                <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Notes</Label>
                                <textarea className="mt-1.5 w-full h-20 px-3 py-2 bg-muted/40 border border-border rounded-md text-[13px]" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} />
                            </div>
                            <div>
                                <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Terms</Label>
                                <textarea className="mt-1.5 w-full h-20 px-3 py-2 bg-muted/40 border border-border rounded-md text-[13px]" value={form.terms || ""} onChange={(e) => set("terms", e.target.value)} />
                            </div>
                        </div>
                    </div>

                    <div className="bg-card border border-border rounded-md p-5 h-fit sticky top-[76px]">
                        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-3">SUMMARY</div>
                        <div className="space-y-2 text-[13px]">
                            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">{fmtMoney(preview.sub, form.currency)}</span></div>
                            <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span className="font-mono">{fmtMoney(preview.tax, form.currency)}</span></div>
                            <div className="flex justify-between"><span className="text-muted-foreground">Shipping</span>
                                <Input className="w-24 h-7 text-right font-mono" type="number" value={form.shipping || 0} onChange={(e) => set("shipping", Number(e.target.value))} />
                            </div>
                            <div className="border-t border-border my-2" />
                            <div className="flex justify-between font-medium text-base"><span>Total</span><span className="serif-num text-2xl">{fmtMoney(preview.total, form.currency)}</span></div>
                            <div className="text-[11px] text-muted-foreground">In base ({form.currency === "INR" ? "INR" : "INR"}): {fmtMoney(preview.total * form.exchange_rate, "INR")}</div>
                        </div>
                    </div>
                </div>
            </PageBody>
        </>
    );
}
